import { useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type {
  Exercise,
  ProgramTargetProjection,
  TargetItem,
  TimedSessionResult,
  UnitsPreference,
} from "@heraion/shared";

import { CreateRow } from "../primitives/CreateRow";
import { EmptyState } from "../primitives/EmptyState";
import { IconButton } from "../primitives/IconButton";
import { Icon } from "../Icon/Icon";
import type { EditorStyles } from "../ProgramEditor/styles";
import type { UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";
import type { PlanStyles } from "./planComposerStyles";
import { WeeklyTargetEditorRow } from "./WeeklyTargetEditorRow";
import { TargetProjectionMatrix } from "./TargetProjectionMatrix";
import {
  AXES,
  METRIC,
  METRICS,
  isScored,
  useWeeklyTargetsEditor,
} from "./useWeeklyTargetsEditor";

// AP-1b/AP-1b2 — the weekly-targets editor VIEW. A program-level foldable band in
// the composer that edits Program.targets (the weekly goals the AP-3 three-zone
// meter scores against). Axes: `muscle` (default — absorbs exercise rotation, a
// hard set of any squat variant counts toward quads) and `exercise` (only the
// program's OWN exercises, for a fixed lift you don't rotate). Metrics =
// hard-sets + tonnage.
//
// The meter now scores muscle/exercise × hard-sets/tonnage (AP-1b-u); only the
// legacy `pattern` axis / `reps` metric carry the "not in meter yet" tag. A
// tonnage goal is edited in the user's unit preference (display lb / save kg),
// and bands are per-target editable (AP-1b-b). All vocab/uniqueness/mutation/
// unit/band logic lives in useWeeklyTargetsEditor; this file is pure render.
// See [[reference_jl_targets_editor]].

type Props = {
  theme: UiTheme;
  cs: ComposerStyles;
  ps: PlanStyles;
  selectStyles: EditorStyles;
  // The program's own exercises — the pool for an `exercise`-axis target.
  programExercises: { slug: string; name: string }[];
  // Optional history + full catalog for the reference values (absent on web).
  sessionResults?: TimedSessionResult[];
  exerciseCatalog?: ReadonlyArray<Exercise>;
  // AP-7: average sets/week per muscle the program prescribes, keyed by muscle
  // slug (programWeeklyPrescription). Shown next to the actual in the editor.
  prescription?: Record<string, number>;
  // AP-10: the day×week projection of every target, for the matrix below the
  // list. Null/undefined (invalid draft, no catalog, no scoreable target) → no
  // matrix, exactly like the prescription readout degrades.
  projection?: ProgramTargetProjection | null;
  // The user's weight-unit preference — a tonnage goal is displayed/entered in
  // it (converted to canonical kg at save). Required.
  unitsPreference: UnitsPreference;
  // PG-2 — the athlete's week-start day, for the "you average X/week" reference.
  weekStartDay?: number;
  items: TargetItem[];
  onChange: (items: TargetItem[]) => void;
  // Whether the band starts expanded. Defaults false — in the composer this is
  // one band among many, so it folds away until you want it. CO-7's coach editor
  // passes true: there the targets ARE the surface, and a fold the coach must
  // open before seeing what he navigated to is pure noise.
  defaultOpen?: boolean;
};

export function WeeklyTargetsSection({
  theme,
  cs,
  ps,
  selectStyles,
  programExercises,
  sessionResults,
  exerciseCatalog,
  prescription,
  projection,
  unitsPreference,
  weekStartDay,
  items,
  onChange,
  defaultOpen = false,
}: Props) {
  const { t } = useTranslation();
  const p = theme.palette;
  const styles = makeStyles(theme);
  const [open, setOpen] = useState(defaultOpen);
  const ed = useWeeklyTargetsEditor({
    items,
    onChange,
    unitsPreference,
    programExercises,
    sessionResults,
    exerciseCatalog,
    prescription,
    weekStartDay,
  });

  const renderRow = (it: TargetItem, i: number) => (
    <Pressable
      key={i}
      style={styles.row}
      onPress={() => ed.beginEdit(i)}
      accessibilityRole="button"
      accessibilityLabel={ed.rowSummary(it)}
    >
      <View style={styles.rowMain}>
        <Text style={styles.rowKey} numberOfLines={2}>
          {ed.keyLabel(it.axis, it.key)}
        </Text>
        <View style={styles.rowMeta}>
          <Text style={styles.rowValue}>{ed.displayValueWithUnit(it)}</Text>
          <Text style={styles.rowBand}>{ed.bandLabel(it)}</Text>
          {!isScored(it) ? (
            <View style={styles.notScoredRow}>
              <Icon name="info" size={theme.typography.sizes.sm} color={p.ink3} />
              <Text style={styles.notScored}>
                {t("composer.targetNotScored", { defaultValue: "not in meter yet" })}
              </Text>
            </View>
          ) : null}
        </View>
      </View>
      <IconButton
        theme={theme}
        name="close"
        variant="cancel"
        onPress={() => ed.remove(i)}
        accessibilityLabel={t("composer.targetRemove", {
          name: ed.keyLabel(it.axis, it.key),
          defaultValue: "Remove target {{name}}",
        })}
      />
    </Pressable>
  );

  const renderEditor = (it: TargetItem, i: number) => (
    <WeeklyTargetEditorRow
      key={i}
      theme={theme}
      cs={cs}
      ps={ps}
      selectStyles={selectStyles}
      title={ed.keyLabel(it.axis, it.key)}
      axis={it.axis}
      axisChips={AXES.map((a) => ({
        key: a,
        label:
          a === "muscle"
            ? t("composer.targetAxisMuscle", { defaultValue: "Muscle" })
            : t("composer.targetAxisExercise", { defaultValue: "Exercise" }),
        selected: it.axis === a,
        disabled: ed.axisDisabled(i, a),
      }))}
      metricChips={METRICS.map((m) => ({
        key: m,
        label: t(METRIC[m].chip),
        selected: it.metric === m,
        disabled: ed.metricTaken(i, m),
      }))}
      keyValue={it.key}
      keyOptions={ed.keyOptions(i)}
      unitLabel={ed.unitLabel(it)}
      bandLabel={ed.bandLabel(it)}
      belowText={ed.belowText}
      aboveText={ed.aboveText}
      scored={isScored(it)}
      valueText={ed.valueText}
      actual={ed.stats}
      prescribed={ed.prescribed}
      formatRef={(n) => ed.formatMetricValue(n, it.metric)}
      belowInvalid={ed.belowInvalid}
      aboveInvalid={ed.aboveInvalid}
      onUseAvg={ed.stats?.hasData ? () => ed.useAvg(i) : undefined}
      onUsePrescribed={
        ed.prescribed != null && ed.prescribed > 0
          ? () => ed.usePrescribed(i)
          : undefined
      }
      onAxis={(a) => ed.changeAxis(i, a)}
      onMetric={(m) => ed.changeMetric(i, m)}
      onKey={(k) => ed.setKey(i, k)}
      onValueChange={(raw) => ed.onValueChange(i, raw)}
      onValueBlur={() => ed.onValueBlur(i)}
      onBelowChange={(raw) => ed.onBelowChange(i, raw)}
      onAboveChange={(raw) => ed.onAboveChange(i, raw)}
      onBandBlur={() => ed.onBandBlur(i)}
      onCollapse={() => ed.setEditingIdx(null)}
    />
  );

  return (
    <View style={cs.card}>
      <Pressable
        style={ps.disclosureBtn}
        onPress={() => setOpen((v) => !v)}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
      >
        <Icon
          name={open ? "chevron-up" : "chevron-down"}
          size={theme.typography.sizes.md}
          color={p.ink3}
        />
        <Text style={ps.disclosureText}>
          {t("composer.targetsTitle", { defaultValue: "Weekly targets" })}
        </Text>
        {items.length > 0 ? <Text style={styles.count}>{items.length}</Text> : null}
      </Pressable>

      {open ? (
        <>
          {items.length === 0 ? (
            <EmptyState
              theme={theme}
              icon="target"
              title={t("composer.targetsEmptyTitle", { defaultValue: "No weekly targets" })}
              sub={t("composer.targetsEmptySub", {
                defaultValue: "Add one to light up the level meter.",
              })}
            />
          ) : (
            items.map((it, i) =>
              ed.editingIdx === i ? renderEditor(it, i) : renderRow(it, i),
            )
          )}

          {projection && items.length > 0 ? (
            <TargetProjectionMatrix
              theme={theme}
              projection={projection}
              unitsPreference={unitsPreference}
              labelFor={(it) => ed.keyLabel(it.axis, it.key)}
              metricLabel={(m) => t(METRIC[m].chip)}
            />
          ) : null}

          <CreateRow
            theme={theme}
            style={{ marginTop: theme.spacing.s2 }}
            label={t("composer.targetAdd", { defaultValue: "Add target" })}
            onPress={() => ed.addTarget()}
          />
        </>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    count: {
      color: p.ink3,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      fontVariant: ["tabular-nums"],
    },
    row: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      minHeight: 44,
      gap: s.s3,
      paddingVertical: s.s2,
      borderTopWidth: 1,
      borderTopColor: p.border,
    },
    rowMain: { flex: 1, gap: 2 },
    rowKey: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    rowMeta: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: s.s2 },
    rowValue: {
      color: p.ink,
      fontSize: t.sizes.base,
      fontWeight: "600",
      fontVariant: ["tabular-nums"],
    },
    rowBand: { color: p.ink3, fontSize: t.sizes.sm, fontVariant: ["tabular-nums"] },
    notScoredRow: { flexDirection: "row", alignItems: "center", gap: 2 },
    notScored: { color: p.ink3, fontSize: t.sizes.sm },
  });
}
